import {
  Box,
  IconButton,
  MenuItem,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  IP_FILTER_OPTIONS,
  MIN_SCORE_FILTER_OPTIONS,
  MULTI_BRAND_FILTER_OPTIONS,
  QUALIFIED_FILTER_OPTIONS,
  getMaxEndDate,
  getMinStartDate,
  isDateRangeInvalid,
} from "../constants";

interface LeadsFilterItemsProps {
  data: LeadsManagementData;
}

function toDayjs(value: string): Dayjs | null {
  return value ? dayjs(value) : null;
}

function toIsoDate(value: Dayjs | null): string {
  return value && value.isValid() ? value.format("YYYY-MM-DD") : "";
}

// DatePicker's own `readOnly` prop disables the popup calendar too, which
// isn't what we want — only keyboard entry should be blocked, selecting via
// the calendar must keep working. Tab is left alone so focus can still move.
function blockTyping(event: React.KeyboardEvent) {
  if (event.key !== "Tab") event.preventDefault();
}

function blockPaste(event: React.ClipboardEvent) {
  event.preventDefault();
}

export default function LeadsFilterItems({ data }: LeadsFilterItemsProps) {
  const {
    brand,
    start,
    end,
    search,
    brandOptions,
    setBrand,
    setStart,
    setEnd,
    setSearch,
    qualified,
    ipFilter,
    minScore,
    multiBrand,
    setQualified,
    setIpFilter,
    setMinScore,
    setMultiBrand,
    isLoading,
  } = data;
  const maxEndDate = getMaxEndDate(start);
  const minStartDate = getMinStartDate(end);

  // A change that would push the range over 365 days (or invert it) is
  // rejected outright — the other date must be adjusted first, it is never
  // auto-adjusted for the user. Typing is blocked on both pickers (see
  // blockTyping/blockPaste below) so this mainly guards picker selections,
  // but it stays as a defensive backstop.
  function handleStartChange(nextStart: string) {
    if (nextStart && isDateRangeInvalid(nextStart, end)) return;
    setStart(nextStart);
  }

  function handleEndChange(nextEnd: string) {
    if (nextEnd && isDateRangeInvalid(start, nextEnd)) return;
    setEnd(nextEnd);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        select
        label="Brand"
        value={brand}
        onChange={(e) => setBrand(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="922"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 230 }}
      >
        {brandOptions.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Qualified by brand"
        value={qualified}
        onChange={(e) => setQualified(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="961"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 170 }}
      >
        {QUALIFIED_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="IP address"
        value={ipFilter}
        onChange={(e) => setIpFilter(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="962"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 170 }}
      >
        {IP_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        label="Multi-brand"
        value={multiBrand}
        onChange={(e) => setMultiBrand(e.target.value)}
        size="small"
        color="secondary"
        disabled={isLoading}
        data-tracker="1039"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 150 }}
      >
        {MULTI_BRAND_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      {/* Disabled until lead_scoring_min is confirmed live on the backend —
          see the note in useLeadsManagementData.ts's fetchLeads. Left wired
          up (value/onChange/options all still work) so re-enabling later is
          just removing `disabled`. */}
      <TextField
        select
        disabled
        label="Min. lead score"
        value={minScore}
        onChange={(e) => setMinScore(e.target.value)}
        size="small"
        color="secondary"
        data-tracker="963"
        title="Coming soon — pending backend support"
        SelectProps={{ MenuProps: { disablePortal: true } }}
        sx={{ minWidth: 150 }}
      >
        {MIN_SCORE_FILTER_OPTIONS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <DatePicker
        label="Start date"
        format="DD/MM/YYYY"
        value={toDayjs(start)}
        onChange={(value) => handleStartChange(toIsoDate(value))}
        minDate={toDayjs(minStartDate ?? "") ?? undefined}
        maxDate={toDayjs(end) ?? undefined}
        disabled={isLoading}
        slotProps={{
          textField: {
            size: "small",
            color: "secondary",
            onKeyDown: blockTyping,
            onPaste: blockPaste,
            "data-tracker": "923",
          } as any,

          popper: { disablePortal: true },
        }}
      />

      <DatePicker
        label="End date"
        format="DD/MM/YYYY"
        value={toDayjs(end)}
        onChange={(value) => handleEndChange(toIsoDate(value))}
        minDate={toDayjs(start) ?? undefined}
        maxDate={toDayjs(maxEndDate ?? "") ?? undefined}
        disabled={isLoading}
        slotProps={{
          textField: {
            size: "small",
            color: "secondary",
            onKeyDown: blockTyping,
            onPaste: blockPaste,
            "data-tracker": "924",
          } as any,
          popper: { disablePortal: true },
        }}
      />

      <Box sx={{ flex: 1, minWidth: 220 }}>
        <TextField
          fullWidth
          label="Search"
          placeholder="Name, email, ID, brand or IP"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          color="secondary"
          disabled={isLoading}
          data-tracker="925"
          InputLabelProps={{ shrink: true }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" sx={{ color: "#9e9e9e" }} />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  aria-label="Clear search"
                  size="small"
                  disabled={isLoading}
                  onClick={() => setSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>
    </LocalizationProvider>
  );
}
